import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home as HomeIcon, AlertOctagon, RefreshCw, Bug } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { api, resolveMediaUrl } from "@/lib/api";
import { trackPageview } from "@/lib/analytics";
import { useLang } from "@/i18n/LangContext";

/**
 * Custom 500 (Server Error) page.
 *
 * Used by:
 *   - The "TEST 500" admin link (route /500 - pure preview).
 *   - The React <ErrorBoundary> fallback (when a render error crashes the SPA).
 *
 * Props (optional, used by ErrorBoundary):
 *   - error: the JS Error instance that was thrown
 *   - onRetry: callback to reset the boundary and try again
 */
export default function ServerError({ error = null, onRetry = null }) {
    const location = useLocation();
    const { t, lp } = useLang();
    const [modInfo, setModInfo] = useState(null);
    const [signUrl, setSignUrl] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/mod-info");
                setModInfo(data);
                setSignUrl(data?.server_error_image_url || "");
            } catch {
                /* ignore - even mod-info may be down when this page renders */
            }
        })();
        trackPageview(location?.pathname || "/500");
    }, [location?.pathname]);

    const handleRetry = () => {
        if (typeof onRetry === "function") {
            onRetry();
            return;
        }
        // Default: reload the page
        if (typeof window !== "undefined") window.location.reload();
    };

    return (
        <div
            data-testid="server-error-page"
            className="min-h-screen flex flex-col bg-[color:var(--bg-base)]"
        >
            <SEO
                modInfo={modInfo}
                pathname="/500"
                title="500 - Błąd serwera"
                description="Wystąpił nieoczekiwany błąd serwera. Spróbuj odświeżyć stronę lub wrócić na stronę główną."
                noindex
            />
            <Header />
            <main className="flex-1 flex items-center pt-32 pb-24">
                <div className="max-w-4xl mx-auto px-6 md:px-12 w-full">
                    <div className="label-eyebrow mb-4 flex items-center gap-2 text-red-300">
                        <AlertOctagon size={14} /> {t("Błąd 500")}
                    </div>

                    {/* Big 500 + warning sign */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10 md:gap-14 mb-6">
                        <h1
                            data-testid="server-error-code"
                            className="font-display uppercase leading-none tracking-tighter text-red-400 text-[22vw] sm:text-[9rem] md:text-[13rem]"
                            style={{ lineHeight: 0.85 }}
                        >
                            500
                        </h1>

                        {/* Custom uploaded sign (from admin) or default warning triangle SVG */}
                        {signUrl ? (
                            <img
                                src={resolveMediaUrl(signUrl)}
                                alt="Znak ostrzegawczy"
                                data-testid="server-error-sign"
                                className="w-40 sm:w-44 md:w-56 shrink-0 object-contain drop-shadow-2xl"
                            />
                        ) : (
                            <svg
                                data-testid="server-error-sign"
                                viewBox="0 0 100 100"
                                role="img"
                                aria-label="Znak ostrzegawczy"
                                className="w-40 sm:w-44 md:w-56 shrink-0 drop-shadow-2xl"
                            >
                                {/* outer triangle (red border) */}
                                <polygon
                                    points="50,6 96,90 4,90"
                                    fill="#d8231d"
                                    stroke="#ffffff"
                                    strokeWidth="3"
                                    strokeLinejoin="round"
                                />
                                {/* inner triangle (yellow / amber) */}
                                <polygon
                                    points="50,18 86,84 14,84"
                                    fill="#facc15"
                                    strokeLinejoin="round"
                                />
                                {/* exclamation mark */}
                                <rect x="46" y="38" width="8" height="26" rx="2" fill="#040914" />
                                <circle cx="50" cy="74" r="4.5" fill="#040914" />
                            </svg>
                        )}
                    </div>

                    <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tighter text-white mb-5">
                        {t("Coś poszło nie tak na trasie")}
                    </h2>

                    <p className="text-base text-[color:var(--text-secondary)] leading-relaxed max-w-xl mb-3">
                        {t(
                            "Wystąpił nieoczekiwany błąd serwera. Mechanik już został powiadomiony - w międzyczasie możesz odświeżyć stronę albo wrócić na stronę główną."
                        )}
                    </p>

                    {error && (
                        <details
                            data-testid="server-error-details"
                            className="mt-2 mb-8 bg-red-500/5 border border-red-500/20 rounded-md p-3 max-w-3xl"
                        >
                            <summary className="text-xs uppercase tracking-wider text-red-300 cursor-pointer">
                                {t("Szczegóły techniczne")}
                            </summary>
                            <pre className="mt-2 text-xs text-red-200/80 whitespace-pre-wrap break-words font-mono leading-relaxed">
                                {String(error?.message || error)}
                                {error?.stack && (
                                    <>
                                        {"\n\n"}
                                        {error.stack}
                                    </>
                                )}
                            </pre>
                        </details>
                    )}

                    <div className="flex flex-wrap gap-3 mt-6">
                        <button
                            type="button"
                            data-testid="server-error-retry-btn"
                            onClick={handleRetry}
                            className="btn-primary"
                        >
                            <RefreshCw size={14} /> {t("Spróbuj ponownie")}
                        </button>
                        <Link
                            to={lp("/")}
                            data-testid="server-error-home-btn"
                            className="inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-wider font-semibold rounded-md border border-white/15 text-[color:var(--text-secondary)] hover:text-white hover:border-white/30 transition-colors"
                        >
                            <HomeIcon size={14} /> {t("Strona główna")}
                        </Link>
                        <Link
                            to={lp("/zglos-blad")}
                            data-testid="server-error-report-btn"
                            className="inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-wider font-semibold rounded-md border border-white/15 text-[color:var(--text-secondary)] hover:text-white hover:border-white/30 transition-colors"
                        >
                            <Bug size={14} /> {t("Zgłoś problem")}
                        </Link>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}
